import {
  VariableFilletControlPointResolver,
  convertMToMm,
  type VariableFilletMode,
  type VariableRadiusControlPoint,
} from '@puppycad/cad-core';

export const DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT = VariableFilletControlPointResolver.DEFAULT_INSTANCE_COUNT;
export const MIN_VARIABLE_FILLET_INSTANCE_COUNT = VariableFilletControlPointResolver.MIN_INSTANCE_COUNT;
export const MAX_VARIABLE_FILLET_INSTANCE_COUNT = VariableFilletControlPointResolver.MAX_INSTANCE_COUNT;

export interface VariableRadiusDisplayItem {
  key: string;
  persistentRef: string;
  itemRef: string;
  label: string;
  role: 'vertex' | 'instance';
  parameter: number;
}

export type VariableRadiusValueSlot = 'radius1' | 'radius2';

const resolver = new VariableFilletControlPointResolver();

/**
 * 把实例数限制在可维护范围内。
 *
 * @param count 原始实例数。
 * @returns 合法实例数。
 */
export function clampVariableRadiusInstanceCount(count: number | null | undefined): number {
  return resolver.clampInstanceCount(count);
}

/**
 * 构建变量圆角控制点键。
 *
 * @param itemRef 目标边引用。
 * @param label 点标签。
 * @returns 控制点键。
 */
export function buildVariableRadiusPointKey(itemRef: string, label: string): string {
  return resolver.buildPointKey(itemRef, label);
}

/**
 * 构建变量圆角实例候选点键。
 *
 * @param itemRef 目标边引用。
 * @param instanceIndex 实例候选序号。
 * @returns 候选点键。
 */
export function buildVariableRadiusInstanceCandidateKey(
  itemRef: string,
  instanceIndex: number,
): string {
  return resolver.buildInstanceCandidateKey(itemRef, instanceIndex);
}

/**
 * 创建变量圆角端点控制点。
 *
 * @param itemRef 目标边引用。
 * @param radius 默认半径。
 * @param expression 默认半径表达式。
 * @returns V 端点控制点。
 */
export function createDefaultVariableRadiusPoints(
  itemRef: string,
  radius: number | null = null,
  expression: string | null = null,
): VariableRadiusControlPoint[] {
  return resolver.ensureEndpointPoints({
    current: [],
    itemRefs: [itemRef],
    radius,
    expression,
  });
}

/**
 * 创建变量圆角实例点。
 *
 * @param itemRef 目标边引用。
 * @param index 从 1 开始的实例点序号。
 * @param instanceCount 当前实例数。
 * @returns 实例控制点。
 */
export function createVariableRadiusInstancePoint(
  itemRef: string,
  index: number,
  instanceCount: number,
): VariableRadiusControlPoint {
  const normalizedCount = Math.max(1, clampVariableRadiusInstanceCount(instanceCount));
  const normalizedIndex = Math.max(1, Math.min(normalizedCount, Math.round(index)));
  const label = `P${normalizedIndex}`;
  return {
    key: buildVariableRadiusPointKey(itemRef, label),
    itemRef,
    label,
    role: 'instance',
    instanceIndex: normalizedIndex,
    parameter: normalizedIndex / (normalizedCount + 1),
    radius: null,
    expression: null,
    radius2: null,
    expression2: null,
  };
}

/**
 * 补齐当前边集的端点控制点，并保留已经加入的合法实例点。
 *
 * @param current 当前控制点。
 * @param itemRefs 当前边引用集合。
 * @param instanceCount 当前实例数。
 * @param radius 默认半径。
 * @param expression 默认半径表达式。
 * @returns 归一化后的控制点集合。
 */
export function ensureVariableRadiusEndpointPoints(
  current: VariableRadiusControlPoint[] | undefined,
  itemRefs: string[],
  instanceCount = DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT,
  radius: number | null = null,
  expression: string | null = null,
): VariableRadiusControlPoint[] {
  return resolver.ensureEndpointPoints({
    current,
    itemRefs,
    instanceCount,
    radius,
    expression,
  });
}

/**
 * 向控制点集合中写入实例点。
 *
 * @param current 当前控制点。
 * @param itemRef 目标边引用。
 * @param index 实例候选点序号。
 * @param instanceCount 当前实例数。
 * @returns 写入后的控制点集合。
 */
export function upsertVariableRadiusInstancePoint(
  current: VariableRadiusControlPoint[] | undefined,
  itemRef: string,
  index: number,
  instanceCount: number,
): VariableRadiusControlPoint[] {
  return upsertVariableRadiusInstancePointWithSelection(
    current,
    itemRef,
    index,
    instanceCount,
  ).points;
}

/**
 * 向控制点集合中写入实例点，并返回实际选中的 P 点 key。
 *
 * @param current 当前控制点。
 * @param itemRef 目标边引用。
 * @param index 实例候选点序号。
 * @param instanceCount 当前实例数。
 * @returns 写入结果。
 */
export function upsertVariableRadiusInstancePointWithSelection(
  current: VariableRadiusControlPoint[] | undefined,
  itemRef: string,
  index: number,
  instanceCount: number,
): { points: VariableRadiusControlPoint[]; selectedKey: string | null } {
  return resolver.upsertInstancePoint({
    current,
    itemRef,
    instanceIndex: index,
    instanceCount,
  });
}

/**
 * 更新控制点半径。
 *
 * @param current 当前控制点。
 * @param key 控制点键。
 * @param radius 半径。
 * @param expression 半径表达式。
 * @param slot 半径槽位。
 * @returns 更新后的控制点集合。
 */
export function updateVariableRadiusPointRadius(
  current: VariableRadiusControlPoint[] | undefined,
  key: string,
  radius: number,
  expression: string | null,
  slot: VariableRadiusValueSlot = 'radius1',
): VariableRadiusControlPoint[] {
  return resolver.updateRadius({
    current,
    key,
    radius,
    expression,
    slot,
  });
}

/**
 * 把指定半径写入所有变量圆角控制点。
 *
 * @param current 当前控制点。
 * @param radius 半径。
 * @param expression 半径表达式。
 * @param radius2 第二半径。
 * @param expression2 第二半径表达式。
 * @returns 更新后的控制点集合。
 */
export function assignVariableRadiusToAllPoints(
  current: VariableRadiusControlPoint[] | undefined,
  radius: number,
  expression: string | null,
  radius2?: number,
  expression2: string | null = null,
): VariableRadiusControlPoint[] {
  return resolver.assignRadiusToAll({
    current,
    radius,
    expression,
    radius2,
    expression2,
  });
}

/**
 * 删除变量圆角实例点。
 *
 * @param current 当前控制点。
 * @param key 控制点 key。
 * @param itemRefs 当前边引用集合。
 * @param instanceCount 当前实例数。
 * @param radius 默认半径。
 * @param expression 默认半径表达式。
 * @returns 删除后的控制点集合。
 */
export function removeVariableRadiusInstancePoint(
  current: VariableRadiusControlPoint[] | undefined,
  key: string,
  itemRefs: string[],
  instanceCount = DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT,
  radius: number | null = null,
  expression: string | null = null,
): VariableRadiusControlPoint[] {
  return resolver.removeInstancePoint({
    current,
    key,
    itemRefs,
    instanceCount,
    radius,
    expression,
  });
}

/**
 * 删除所有变量圆角实例点。
 *
 * @param current 当前控制点。
 * @param itemRefs 当前边引用集合。
 * @param instanceCount 当前实例数。
 * @param radius 默认半径。
 * @param expression 默认半径表达式。
 * @returns 删除后的控制点集合。
 */
export function removeAllVariableRadiusInstancePoints(
  current: VariableRadiusControlPoint[] | undefined,
  itemRefs: string[],
  instanceCount = DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT,
  radius: number | null = null,
  expression: string | null = null,
): VariableRadiusControlPoint[] {
  return resolver.removeAllInstancePoints({
    current,
    itemRefs,
    instanceCount,
    radius,
    expression,
  });
}

/**
 * 解析变量半径列表显示项。
 *
 * @param points 当前控制点。
 * @returns 可显示列表项。
 */
export function resolveVariableRadiusDisplayItems(
  points: VariableRadiusControlPoint[] | undefined,
  mode: VariableFilletMode = 'symmetric',
): VariableRadiusDisplayItem[] {
  const displayByKey = new Map<string, VariableRadiusControlPoint>();
  resolver.sortControlPoints(points ?? []).forEach((point) => {
    const label = resolver.resolvePointLabel(point);
    const displayKey = point.role === 'vertex' ? label : point.key;
    if (!displayByKey.has(displayKey)) {
      displayByKey.set(displayKey, point);
    }
  });
  return Array.from(displayByKey.values()).map((point) => ({
    key: point.key,
    persistentRef: point.key,
    itemRef: point.itemRef,
    label: formatVariableRadiusDisplayLabel(point, mode),
    role: point.role === 'instance' ? 'instance' : 'vertex',
    parameter: point.parameter,
  }));
}

/**
 * 按 SolidWorks 变量圆角半径点列表语义生成显示文本。
 *
 * 对称模式只有一个半径槽位，列表使用 `R`；非对称模式与固定大小圆角一致，
 * 需要明确暴露支撑面两侧的 `R1/R2`，避免第二半径虽然已写入数据却在界面上不可见。
 *
 * @param point 变量半径控制点。
 * @param mode 当前变量圆角模式。
 * @returns 列表显示文本。
 */
function formatVariableRadiusDisplayLabel(
  point: VariableRadiusControlPoint,
  mode: VariableFilletMode,
): string {
  const label = resolver.resolvePointLabel(point);
  if (mode === 'asymmetric') {
    return `${label}, R1 = ${formatVariableRadius(point.radius)}, R2 = ${formatVariableRadius(point.radius2)}`;
  }
  return `${label}, R = ${formatVariableRadius(point.radius)}`;
}

/**
 * 选择默认变量半径控制点。
 *
 * @param points 当前控制点。
 * @param preferredKey 优先 key。
 * @returns 控制点 key。
 */
export function resolveSelectedVariableRadiusPointKey(
  points: VariableRadiusControlPoint[] | undefined,
  preferredKey: string | null | undefined,
): string | null {
  const displayItems = resolveVariableRadiusDisplayItems(points);
  if (preferredKey && displayItems.some((item) => item.key === preferredKey)) {
    return preferredKey;
  }
  return displayItems[0]?.key ?? null;
}

/**
 * 按标签解析实例点序号。
 *
 * @param label 点标签。
 * @returns 实例点序号。
 */
export function parseVariableRadiusInstanceIndex(label: string): number | null {
  return resolver.parseInstanceLabelIndex(label);
}

/**
 * 解析实例候选点 key。
 *
 * @param key 候选点 key。
 * @returns 候选点解析结果。
 */
export function parseVariableRadiusInstanceCandidateKey(
  key: string,
): { itemRef: string; instanceIndex: number } | null {
  return resolver.parseInstanceCandidateKey(key);
}

/**
 * 从控制点键中解析末尾标签。
 *
 * @param key 控制点键。
 * @returns 控制点标签。
 */
export function parseVariableRadiusPointLabelFromKey(key: string): string {
  return resolver.parsePointLabelFromKey(key);
}

/**
 * 格式化半径文本。
 *
 * @param radius 半径。
 * @returns 显示文本。
 */
function formatVariableRadius(radius: number | null | undefined): string {
  if (typeof radius !== 'number' || !Number.isFinite(radius) || radius <= 0) {
    return '未指定';
  }
  const radiusMm = convertMToMm(radius);
  return Number.isInteger(radiusMm) ? `${radiusMm}mm` : `${radiusMm.toFixed(3)}mm`;
}
